import { MarketingShell } from "@/components/MarketingShell";

const ENTITY = "FastCollab Systems Private Limited";

/** Shared legal-page chrome used by Privacy / Terms / DPA. */
function LegalShell({ eyebrow, title, lastUpdated, children, testId }) {
  return (
    <MarketingShell>
      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 lg:py-20" data-testid={testId}>
          <p className="label-caps text-[#3B82F6]">{eyebrow}</p>
          <h1 className="font-['Geist'] font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-2 leading-[1.05] text-[#0F172A]">
            {title}
          </h1>
          <p className="mt-3 font-mono text-[11px] tracking-[0.18em] text-[#64748B] uppercase">
            Last updated · {lastUpdated} · {ENTITY}
          </p>
          <div className="prose-legal mt-10 space-y-7 text-[15px] leading-relaxed text-[#1F2937]">
            {children}
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}

const H = ({ children }) => (
  <h2 className="font-['Geist'] font-semibold text-xl tracking-tight text-[#0F172A] border-t border-[#E5E7EB] pt-6">
    {children}
  </h2>
);
const P = ({ children }) => <p className="text-[#374151]">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-5 space-y-1.5 text-[#374151]">{children}</ul>;

// ---------------------------------------------------------------- PRIVACY
export function PrivacyPolicyPage() {
  return (
    <LegalShell
      testId="privacy-policy-page"
      eyebrow="Legal"
      title="Privacy Policy"
      lastUpdated="14 February 2026"
    >
      <P>
        This Privacy Policy explains how {ENTITY} ("we", "us", "our"), the operator of the Probe test
        automation platform ("Service"), collects, uses, and protects information about you ("you", "user")
        when you create an account, configure a workspace, record tests, or otherwise interact with the Service.
      </P>

      <H>1. Information we collect</H>
      <UL>
        <li>Account data: name, email, hashed password, role, account status, and avatar (if provided).</li>
        <li>Workspace data: workspace name, members, invite tokens, audit logs of admin actions.</li>
        <li>Test artefacts: target URLs, steps you record or generate, run results, screenshots, encrypted login states.</li>
        <li>Operational telemetry: IP address, user-agent, login timestamps and emulation audit events.</li>
      </UL>

      <H>2. How we use your information</H>
      <UL>
        <li>To operate the Service — run tests, store results, and authenticate sessions.</li>
        <li>To secure your account — rate-limit logins, detect abuse and log super-admin emulation events.</li>
        <li>To communicate operational and security notices (we do not send marketing email by default).</li>
      </UL>

      <H>3. Sharing</H>
      <P>
        We do not sell personal data. We share data only with subprocessors strictly required to deliver the
        Service (e.g. Browserbase for cloud browsers and our LLM provider for AI test generation, where you opt in).
        A current subprocessor list is available on request from{" "}
        <a href="mailto:privacy@fastcollab.com" className="text-[#3B82F6] hover:text-[#2563EB] underline underline-offset-2">privacy@fastcollab.com</a>.
      </P>

      <H>4. Storage and security</H>
      <P>
        Login states and platform secrets are encrypted at rest using AES-128 (Fernet). Passwords are
        salted and hashed with bcrypt. Access to production systems is restricted to authorised personnel.
      </P>

      <H>5. Retention</H>
      <P>
        Test artefacts are retained for the lifetime of your account. When you delete your account, your
        records are soft-deleted and access is revoked. We may retain audit logs for up to 24 months to
        satisfy our legal and contractual obligations.
      </P>

      <H>6. Your rights</H>
      <P>
        You may request access, correction, export or deletion of your personal data at any time by writing to{" "}
        <a href="mailto:privacy@fastcollab.com" className="text-[#3B82F6] hover:text-[#2563EB] underline underline-offset-2">privacy@fastcollab.com</a>.
        Where required by law, we will respond within 30 days.
      </P>

      <H>7. Changes to this policy</H>
      <P>
        We will post material updates to this page and, where appropriate, notify you by email. Continued
        use of the Service after the effective date constitutes acceptance of the revised policy.
      </P>

      <H>8. Contact</H>
      <P>
        {ENTITY} · <a href="mailto:privacy@fastcollab.com" className="text-[#3B82F6] hover:text-[#2563EB] underline underline-offset-2">privacy@fastcollab.com</a>
      </P>
    </LegalShell>
  );
}

// ---------------------------------------------------------------- TERMS
export function TermsOfServicePage() {
  return (
    <LegalShell
      testId="terms-of-service-page"
      eyebrow="Legal"
      title="Terms of Service"
      lastUpdated="14 February 2026"
    >
      <P>
        These Terms of Service ("Terms") govern your access to and use of the Probe platform operated by{" "}
        {ENTITY} ("Probe", "we", "us"). By creating an account or using the Service you agree to be bound by these Terms.
      </P>

      <H>1. Accounts</H>
      <P>
        You must provide accurate registration details and keep your credentials secure. You are responsible
        for all activity that occurs under your account. New accounts begin in a Pending state and may be
        activated, rejected, suspended or archived at the sole discretion of {ENTITY}.
      </P>

      <H>2. Acceptable use</H>
      <UL>
        <li>You will only run automated tests against web properties you own or are authorised to test.</li>
        <li>You will not use the Service to perform credential stuffing, denial-of-service attacks or unlawful scraping.</li>
        <li>You will not reverse-engineer the Service or attempt to circumvent its security features.</li>
      </UL>

      <H>3. Subscription &amp; billing</H>
      <P>
        Paid plans are billed in advance on a monthly or annual cycle. You may cancel at any time; the
        cancellation takes effect at the end of the current billing period. Fees are non-refundable
        except where required by law.
      </P>

      <H>4. Intellectual property</H>
      <P>
        Probe, including its software, models, prompts and brand assets, is and remains the exclusive
        property of {ENTITY}. You retain ownership of the test cases, recordings and data you upload
        ("Customer Content"). You grant us a limited licence to host, process and display Customer Content
        solely as required to operate the Service for you.
      </P>

      <H>5. Confidentiality</H>
      <P>
        Each party will protect the other's confidential information with the same care it uses for its own.
        Customer Content is treated as your confidential information.
      </P>

      <H>6. Warranty disclaimer</H>
      <P>
        The Service is provided "as is" without warranty of any kind, express or implied. {ENTITY} does
        not warrant that the Service will be uninterrupted, error-free or that AI-generated tests will be
        correct in all cases.
      </P>

      <H>7. Limitation of liability</H>
      <P>
        To the maximum extent permitted by law, {ENTITY}'s aggregate liability arising out of or related
        to these Terms will not exceed the fees you paid to us in the 12 months immediately preceding the event giving rise to the claim.
      </P>

      <H>8. Termination</H>
      <P>
        We may suspend or terminate your access to the Service at any time for material breach of these
        Terms. Upon termination, your right to use the Service immediately ceases.
      </P>

      <H>9. Governing law</H>
      <P>
        These Terms are governed by the laws of India, without regard to its conflict-of-laws principles.
        Disputes will be resolved exclusively in the courts located in Bengaluru, India.
      </P>

      <H>10. Contact</H>
      <P>
        {ENTITY} · <a href="mailto:legal@fastcollab.com" className="text-[#3B82F6] hover:text-[#2563EB] underline underline-offset-2">legal@fastcollab.com</a>
      </P>
    </LegalShell>
  );
}

// ---------------------------------------------------------------- DPA
export function DPAPage() {
  return (
    <LegalShell
      testId="dpa-page"
      eyebrow="Legal"
      title="Data Processing Addendum"
      lastUpdated="14 February 2026"
    >
      <P>
        This Data Processing Addendum ("DPA") forms part of, and is subject to, the Terms of Service between
        the Customer ("Controller") and {ENTITY} ("Processor"). It governs the Processing of Personal Data
        carried out by the Processor on behalf of the Controller in connection with the Probe platform.
      </P>

      <H>1. Roles &amp; scope</H>
      <P>
        For data submitted by the Controller via the Service, the Controller acts as the data controller
        and {ENTITY} acts as the data processor. The Processor will Process Personal Data only on documented
        instructions from the Controller, including with regard to transfers to a third country.
      </P>

      <H>2. Categories of data &amp; data subjects</H>
      <UL>
        <li>Data subjects: the Controller's end users and the Controller's employees who interact with the Service.</li>
        <li>Personal data: identifiers (email, name), authentication metadata, IP addresses, test artefacts and any data the Controller chooses to upload as part of a recorded test.</li>
      </UL>

      <H>3. Security measures</H>
      <UL>
        <li>Encryption at rest (AES-128 / Fernet) for credentials, login state vaults and platform secrets.</li>
        <li>TLS in transit for all customer-facing endpoints.</li>
        <li>Bcrypt password hashing, brute-force lockout, audit logs of all super-admin emulation events.</li>
        <li>Role-based access control with a least-privilege model.</li>
      </UL>

      <H>4. Subprocessors</H>
      <P>
        The Processor maintains a list of approved subprocessors. The current list is available on request.
        The Processor will provide reasonable prior notice of any intended change of subprocessor and a
        reasonable period for the Controller to object to that change.
      </P>

      <H>5. International transfers</H>
      <P>
        Where Personal Data is transferred outside the European Economic Area, the Processor relies on the
        Standard Contractual Clauses adopted by the European Commission, supplemented by additional
        safeguards where required.
      </P>

      <H>6. Data subject requests</H>
      <P>
        The Processor will, taking into account the nature of the Processing, assist the Controller by
        appropriate technical and organisational measures, insofar as this is possible, for the fulfilment
        of the Controller's obligation to respond to requests for exercising data subject rights.
      </P>

      <H>7. Breach notification</H>
      <P>
        The Processor will notify the Controller without undue delay (and in no event later than 72 hours)
        after becoming aware of a Personal Data Breach affecting the Controller's Personal Data.
      </P>

      <H>8. Return &amp; deletion</H>
      <P>
        Upon termination of the Service, the Processor will, at the choice of the Controller, delete or
        return all Personal Data to the Controller and delete existing copies unless legal obligations
        require continued storage.
      </P>

      <H>9. Contact</H>
      <P>
        {ENTITY} · <a href="mailto:dpo@fastcollab.com" className="text-[#3B82F6] hover:text-[#2563EB] underline underline-offset-2">dpo@fastcollab.com</a>
      </P>
    </LegalShell>
  );
}
